import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../../apiClient';
import BookCover from './BookCover';
import './ReadingTimeline.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const VIEW_MODES = [
  { id: 'chronological', emoji: '', key: 'chronological' },
  { id: 'genre', emoji: '', key: 'byGenre' },
  { id: 'year', emoji: '', key: 'byYear' },
  { id: 'difficulty', emoji: '', key: 'byDifficulty' },
];

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// eslint-disable-next-line no-unused-vars
function formatMonthYear(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function getYear(dateStr) {
  if (!dateStr) return 'Unknown';
  return new Date(dateStr).getFullYear().toString();
}

function renderStars(count, max = 5) {
  const full = Math.round(count || 0);
  return (
    <span className="reading-timeline-stars">
      {'★'.repeat(Math.min(full, max))}{'☆'.repeat(Math.max(0, max - full))}
    </span>
  );
}

function isOnThisDay(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate() &&
    d.getFullYear() < now.getFullYear();
}

function getDifficultyLabel(score) {
  if (!score) return null;
  if (score <= 1) return 'Easy';
  if (score <= 2) return 'Light';
  if (score <= 3) return 'Moderate';
  if (score <= 4) return 'Challenging';
  return 'Difficult';
}

function computeYearStats(books) {
  if (!books.length) return null;
  const totalDifficulty = books.reduce((sum, b) => sum + (b.difficultyRating || b.book?.difficultyScore || 0), 0);
  const ratedCount = books.filter(b => b.difficultyRating || b.book?.difficultyScore).length;
  const genreCounts = {};
  books.forEach(b => {
    const genre = b.book?.genre || b.book?.bookType || 'Unknown';
    genreCounts[genre] = (genreCounts[genre] || 0) + 1;
  });
  const topGenre = Object.entries(genreCounts).sort((a, b) => b[1] - a[1])[0];
  return {
    count: books.length,
    avgDifficulty: ratedCount ? (totalDifficulty / ratedCount).toFixed(1) : null,
    topGenre: topGenre ? topGenre[0] : null,
  };
}

function ReadingTimeline({ factFictionMode, navigateTo }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [books, setBooks] = useState([]);
  const [journals, setJournals] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('chronological');
  const [memoryLane, setMemoryLane] = useState(false);
  const [memoryIndex, setMemoryIndex] = useState(0);
  const timelineRef = useRef(null);
  const entryRefs = useRef([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [booksData, journalData, achieveData] = await Promise.all([
        apiFetchJson(instance, '/api/userbookstatus?status=Finished').catch(() => null),
        apiFetchJson(instance, '/api/journal').catch(() => null),
        apiFetchJson(instance, '/api/gamification/achievements/my').catch(() => null),
      ]);

      if (booksData) {
        setBooks(unwrap(booksData));
      }
      if (journalData) {
        setJournals(unwrap(journalData));
      }
      if (achieveData) {
        const earned = unwrap(achieveData?.achievements || achieveData?.$values || achieveData)
          .filter(a => a.earned);
        setAchievements(earned);
      }
    } catch (err) {
      setError('Failed to load timeline data.');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const journalMap = {};
  journals.forEach(j => {
    if (j.bookId) journalMap[j.bookId] = j;
  });

  const sortedBooks = [...books].sort((a, b) => {
    const da = a.finishDate ? new Date(a.finishDate) : new Date(0);
    const db = b.finishDate ? new Date(b.finishDate) : new Date(0);
    return db - da;
  });

  const getGroupedEntries = () => {
    if (viewMode === 'genre') {
      const groups = {};
      sortedBooks.forEach(b => {
        const genre = b.book?.genre || b.book?.bookType || 'Unknown';
        if (!groups[genre]) groups[genre] = [];
        groups[genre].push(b);
      });
      return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
    }
    if (viewMode === 'difficulty') {
      const groups = { 'Easy (1-2)': [], 'Moderate (3)': [], 'Challenging (4-5)': [] };
      sortedBooks.forEach(b => {
        const d = b.difficultyRating || b.book?.difficultyScore || 0;
        if (d <= 2) groups['Easy (1-2)'].push(b);
        else if (d <= 3) groups['Moderate (3)'].push(b);
        else groups['Challenging (4-5)'].push(b);
      });
      return Object.entries(groups).filter(([, v]) => v.length > 0);
    }
    // Chronological and By Year both group by year
    const groups = {};
    sortedBooks.forEach(b => {
      const year = getYear(b.finishDate);
      if (!groups[year]) groups[year] = [];
      groups[year].push(b);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  };

  const groupedEntries = getGroupedEntries();

  // Build merged timeline with achievements interleaved
  const buildTimeline = (booksInGroup) => {
    const entries = booksInGroup.map(b => ({
      type: 'book',
      date: b.finishDate,
      data: b,
    }));

    if (viewMode === 'chronological' || viewMode === 'year') {
      achievements.forEach(a => {
        if (a.earnedAt || a.createdAt) {
          entries.push({
            type: 'achievement',
            date: a.earnedAt || a.createdAt,
            data: a,
          });
        }
      });
    }

    entries.sort((a, b) => {
      const da = a.date ? new Date(a.date) : new Date(0);
      const db = b.date ? new Date(b.date) : new Date(0);
      return db - da;
    });

    return entries;
  };

  const handleMemoryLane = () => {
    if (memoryLane) {
      setMemoryLane(false);
      setMemoryIndex(0);
      return;
    }
    setMemoryLane(true);
    setMemoryIndex(0);
    if (entryRefs.current[0]) {
      entryRefs.current[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleMemoryNext = () => {
    const nextIdx = memoryIndex + 1;
    if (nextIdx < entryRefs.current.length && entryRefs.current[nextIdx]) {
      setMemoryIndex(nextIdx);
      entryRefs.current[nextIdx].scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setMemoryLane(false);
      setMemoryIndex(0);
    }
  };

  const handleMemoryPrev = () => {
    const prevIdx = memoryIndex - 1;
    if (prevIdx >= 0 && entryRefs.current[prevIdx]) {
      setMemoryIndex(prevIdx);
      entryRefs.current[prevIdx].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  if (loading) {
    return (
      <div className="reading-timeline-empty">
        <span className="reading-timeline-empty-icon">⏳</span>
        <p>{t('bookClub.readingTimeline.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reading-timeline-empty">
        <span className="reading-timeline-empty-icon">⚠️</span>
        <p>{error}</p>
        <button className="reading-timeline-btn" onClick={fetchData}>{t('bookClub.common.retry')}</button>
      </div>
    );
  }

  if (sortedBooks.length === 0) {
    return (
      <div className="reading-timeline-empty">
        <span className="reading-timeline-empty-icon">📖</span>
        <p>{t('bookClub.readingTimeline.empty')}</p>
        {navigateTo && <button className="bookclub-nav-link" onClick={() => navigateTo('search')}>🔎 {t('bookClub.common.searchBooks')}</button>}
      </div>
    );
  }

  // "On this day" highlights
  const onThisDayBooks = sortedBooks.filter(b => isOnThisDay(b.finishDate));

  let entryCounter = 0;

  return (
    <div className="reading-timeline-container" ref={timelineRef}>
      <div className="reading-timeline-header">
        <h2 className="reading-timeline-title">📚 {t('bookClub.readingTimeline.title')}</h2>
        <div className="reading-timeline-controls">
          <div className="reading-timeline-view-toggles">
            {VIEW_MODES.map(mode => (
              <button
                key={mode.id}
                className={`reading-timeline-toggle ${viewMode === mode.id ? 'reading-timeline-toggle--active' : ''}`}
                onClick={() => setViewMode(mode.id)}
              >
                {mode.emoji && `${mode.emoji} `}{t('bookClub.readingTimeline.viewModes.' + mode.key)}
              </button>
            ))}
          </div>
          <button
            className={`reading-timeline-btn reading-timeline-btn--memory ${memoryLane ? 'reading-timeline-btn--active' : ''}`}
            onClick={handleMemoryLane}
          >
            {memoryLane ? '✕ ' + t('bookClub.readingTimeline.exitMemoryLane') : '🎞️ ' + t('bookClub.readingTimeline.memoryLane')}
          </button>
        </div>
      </div>

      {memoryLane && (
        <div className="reading-timeline-memory-nav">
          <button
            className="reading-timeline-btn"
            onClick={handleMemoryPrev}
            disabled={memoryIndex <= 0}
          >
            ← {t('bookClub.readingTimeline.previous')}
          </button>
          <span className="reading-timeline-memory-counter">
            {memoryIndex + 1} / {entryRefs.current.length}
          </span>
          <button className="reading-timeline-btn" onClick={handleMemoryNext}>
            {t('bookClub.readingTimeline.next')} →
          </button>
        </div>
      )}

      {onThisDayBooks.length > 0 && (
        <div className="reading-timeline-on-this-day">
          <h3 className="reading-timeline-on-this-day-title">📅 {t('bookClub.readingTimeline.onThisDay')}</h3>
          {onThisDayBooks.map(b => (
            <div key={b.id} className="reading-timeline-on-this-day-card">
              <BookCover src={b.book?.coverImageUrl} alt={b.book?.title} className="reading-timeline-cover reading-timeline-cover--small" emoji="📕" />
              <div>
                <strong>{b.book?.title}</strong> by {b.book?.author}
                <div className="reading-timeline-on-this-day-meta">
                  Finished {formatDate(b.finishDate)} — {t('bookClub.readingTimeline.yearsAgo', { count: new Date().getFullYear() - new Date(b.finishDate).getFullYear() })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="reading-timeline-line">
        {groupedEntries.map(([groupLabel, groupBooks]) => {
          const stats = computeYearStats(groupBooks);
          const timeline = buildTimeline(groupBooks);

          return (
            <div key={groupLabel} className="reading-timeline-group">
              <div className="reading-timeline-year-header">
                <span className="reading-timeline-year-label">{groupLabel}</span>
                <span className="reading-timeline-year-count">
                  {groupBooks.length} {t('bookClub.common.books')}
                </span>
              </div>

              {(viewMode === 'year' || viewMode === 'chronological') && stats && (
                <div className="reading-timeline-year-stats">
                  {stats.avgDifficulty && (
                    <span className="reading-timeline-stat">
                      {t('bookClub.readingTimeline.avgDifficulty')} {stats.avgDifficulty}/5
                    </span>
                  )}
                  {stats.topGenre && (
                    <span className="reading-timeline-stat">
                      {t('bookClub.readingTimeline.topGenre')} {stats.topGenre}
                    </span>
                  )}
                </div>
              )}

              {timeline.map((entry, idx) => {
                const refIdx = entryCounter++;

                if (entry.type === 'achievement') {
                  return (
                    <div
                      key={`ach-${entry.data.id}`}
                      className="reading-timeline-entry reading-timeline-entry--achievement"
                      ref={el => (entryRefs.current[refIdx] = el)}
                    >
                      <div className="reading-timeline-date-col">
                        <span className="reading-timeline-date">{formatDate(entry.date)}</span>
                        <span className="reading-timeline-dot reading-timeline-dot--achievement" />
                      </div>
                      <div className="reading-timeline-card reading-timeline-card--achievement">
                        <span className="reading-timeline-achievement-icon">
                          {entry.data.icon || '🏆'}
                        </span>
                        <div className="reading-timeline-achievement-info">
                          <strong>{t('bookClub.readingTimeline.unlocked')} {entry.data.name}</strong>
                          {entry.data.description && (
                            <p className="reading-timeline-achievement-desc">{entry.data.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                const b = entry.data;
                const journal = journalMap[b.bookId];
                const onThisDay = isOnThisDay(b.finishDate);
                const isHighlighted = memoryLane && refIdx === memoryIndex;

                return (
                  <div
                    key={b.id}
                    className={`reading-timeline-entry${onThisDay ? ' reading-timeline-entry--highlight' : ''}${isHighlighted ? ' reading-timeline-entry--memory-active' : ''}`}
                    ref={el => (entryRefs.current[refIdx] = el)}
                  >
                    <div className="reading-timeline-date-col">
                      <span className="reading-timeline-date">{formatDate(b.finishDate)}</span>
                      <span className="reading-timeline-dot" />
                    </div>
                    <div className="reading-timeline-card">
                      <BookCover src={b.book?.coverImageUrl} alt={b.book?.title} className="reading-timeline-cover" emoji="📕" />
                      <div className="reading-timeline-card-body">
                        <h4 className="reading-timeline-book-title">{b.book?.title}</h4>
                        <p className="reading-timeline-book-author">{b.book?.author}</p>
                        {b.rating != null && b.rating > 0 && (
                          <div className="reading-timeline-rating">
                            <span className="reading-timeline-rating-label">{t('bookClub.readingTimeline.rating')}</span>
                            {renderStars(b.rating)}
                          </div>
                        )}
                        {(b.difficultyRating || b.book?.difficultyScore) && (
                          <div className="reading-timeline-difficulty">
                            <span className="reading-timeline-difficulty-label">
                              {t('bookClub.readingTimeline.difficultyLabel')} ({getDifficultyLabel(b.difficultyRating || b.book?.difficultyScore)})
                            </span>
                            {renderStars(b.difficultyRating || b.book?.difficultyScore)}
                          </div>
                        )}
                        {b.moodTags && b.moodTags.length > 0 && (
                          <div className="reading-timeline-moods">
                            {unwrap(b.moodTags).map((tag, i) => (
                              <span key={i} className="reading-timeline-mood-tag">{tag}</span>
                            ))}
                          </div>
                        )}
                        {journal && (
                          <div>
                            <p className="reading-timeline-journal-snippet">
                              📝 {journal.content?.substring(0, 100)}{journal.content?.length > 100 ? '…' : ''}
                            </p>
                            {navigateTo && <button className="bookclub-nav-link" onClick={() => navigateTo('journal')}>📓 {t('bookClub.readingTimeline.viewInJournal')}</button>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ReadingTimeline;
